import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Loader2,
  ArrowLeft,
  Calendar,
  Building2,
  RefreshCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RenewSubscription() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);

  // Check if returning from payment
  const isSuccess = window.location.pathname.includes('/success');
  const isCancelled = window.location.pathname.includes('/cancelled');

  useEffect(() => {
    checkAccountStatus();
  }, []);

  const checkAccountStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/account-status`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setAccountStatus(data);
      }
    } catch (error) {
      console.error('Error checking account status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRenew = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/renew`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to initiate renewal');
      }

      const data = await res.json();

      // Redirect to PayFast
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.gateway_url;

      Object.entries(data.payment_params).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
      });

      document.body.appendChild(form);
      toast.success('Redirecting to PayFast...');
      form.submit();
    } catch (error) {
      toast.error(error.message);
      setSubmitting(false);
    }
  };

  // Success page
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Subscription Renewed!</h1>
            <p className="text-slate-600 mb-6">
              Your subscription has been successfully renewed for another year. Thank you for continuing with BodyCorp365!
            </p>
            <Link to="/dashboard">
              <Button className="w-full">Go to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Cancelled page
  if (isCancelled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Renewal Cancelled</h1>
            <p className="text-slate-600 mb-6">
              Your renewal was cancelled. Your account will be frozen when your current subscription expires.
            </p>
            <div className="space-y-3">
              <Button onClick={() => navigate('/renew-subscription')} className="w-full">
                Try Again
              </Button>
              <Link to="/dashboard">
                <Button variant="outline" className="w-full">Back to Dashboard</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-100">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="BodyCorp365" className="h-8" />
            <span className="text-xl font-bold text-slate-900">BodyCorp365</span>
          </Link>
          <Link to="/dashboard">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-12">
        <Card className={accountStatus?.status === 'expired' ? 'border-red-200' : ''}>
          <CardHeader className="text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              accountStatus?.status === 'expired' ? 'bg-red-100' : 'bg-blue-100'
            }`}>
              {accountStatus?.status === 'expired' ? (
                <AlertTriangle className="w-8 h-8 text-red-600" />
              ) : (
                <RefreshCw className="w-8 h-8 text-blue-600" />
              )}
            </div>
            <CardTitle className="text-2xl">
              {accountStatus?.status === 'expired' ? 'Account Expired' : 'Renew Subscription'}
            </CardTitle>
            <CardDescription>
              {accountStatus?.status === 'expired' 
                ? 'Your subscription has expired. Renew now to restore access.'
                : accountStatus?.status === 'expiring_soon'
                  ? `Your subscription expires in ${accountStatus.days_until_expiry} days`
                  : 'Renew your annual subscription to continue using BodyCorp365'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status Info */}
            {accountStatus?.status === 'expired' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">
                  <strong>Account Frozen:</strong> Your account was frozen {accountStatus.expired_days_ago} days ago. 
                  Renew now to restore full access to your building management features.
                </p>
              </div>
            )}

            {accountStatus?.expiry_date && accountStatus?.status !== 'expired' && (
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-500" />
                  <div>
                    <p className="text-sm text-slate-500">Current Expiry Date</p>
                    <p className="font-semibold text-slate-900">
                      {new Date(accountStatus.expiry_date).toLocaleDateString('en-ZA', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Renewal Info */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-slate-900 mb-2">Annual Subscription Includes:</h3>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>✓ Levy billing & payment tracking</li>
                <li>✓ Maintenance ticket management</li>
                <li>✓ Document vault & insurance claims</li>
                <li>✓ Trustee communication hub</li>
                <li>✓ Analytics & reporting</li>
                <li>✓ Unlimited users</li>
              </ul>
            </div>

            {/* Renew Button */}
            <Button 
              onClick={handleRenew} 
              className="w-full" 
              size="lg"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Renew Subscription
                </>
              )}
            </Button>

            <p className="text-xs text-slate-500 text-center">
              You will be redirected to PayFast to complete your payment securely.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
